<template>
  <b-modal
    id="give-recognition-modal"
    ref="modalRef"
    size="lg"
    centered
    hide-footer
    body-class="p-0"
    @show="loadBudgetInputs"
    @hidden="resetModal"
  >
    <!-- Stacked rather than inline: .modal-title is centred with a hand-tuned
         offset for the close button, which an inline pill would throw off. -->
    <template #modal-title>
      <div class="modal-title-stack">
        <span>{{ modalTitle }}</span>
        <active-company-pill />
      </div>
    </template>

    <div class="px-4 py-4 step-content">
      <div class="progress-inline">
        <b-progress :value="displayStep" :max="totalSteps" height="4px" class="progress-inline__bar" />
        <span class="progress-inline__text">{{ displayStep }} of {{ totalSteps }}</span>
      </div>

      <award-step
        v-if="step === STEP.AWARD"
        :programs="programs"
        :selected-program="selectedProgram"
        @select="selectedProgram = $event"
      />

      <recipients-step
        v-else-if="step === STEP.RECIPIENTS"
        ref="recipientsStepRef"
        :program="selectedProgram"
        :selected-employees="selectedEmployees"
        @add="addEmployee"
        @remove="removeEmployee"
      />

      <details-step
        v-else-if="step === STEP.DETAILS"
        ref="detailsStepRef"
        :program="selectedProgram"
        :recipient-count="selectedEmployees.length"
        :message="recipientMessage"
        :selected-company-values="selectedCompanyValues"
        :include-reward="includeReward"
        :amount-minor="selectedAmountMinor"
        :typed-amount="typedAwardAmount"
        :category="selectedCategory"
        :is-private="isPrivate"
        :amount-error="adjustableAmountError"
        :validation="budgetValidation"
        :is-checking-budget="isCheckingBudget"
        :error-message="errorMessage"
        @update:message="recipientMessage = $event"
        @update:selectedCompanyValues="selectedCompanyValues = $event"
        @update:amountMinor="selectedAmountMinor = $event"
        @update:typedAmount="onTypedAmountInput"
        @update:category="selectedCategory = $event"
        @update:isPrivate="isPrivate = $event"
        @toggle-reward="onRewardToggle"
      />

      <confirm-step
        v-else
        :employees="selectedEmployees"
        :amount-per-person-minor="includeReward ? selectedAmountMinor : null"
        :error-message="errorMessage"
        @remove="removeEmployee"
      />
    </div>

    <div class="px-4 py-3 footer-container">
      <div>
        <b-button v-if="previousStep !== null" variant="outline-secondary" @click="goBack">Back</b-button>
      </div>
      <div class="d-flex">
        <b-button variant="primary" :disabled="!canAdvance" @click="onAdvance">
          <b-spinner v-if="isSubmitting || isCheckingBudget" small class="mr-1" />
          {{ advanceButtonText }}
        </b-button>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { computed, ref, watch } from 'vue';
import ApiClient, { apiErrorData } from '@/ApiClient';
import State from '@/state/State';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import AwardStep from '@/pages/recognition/components/give-recognition/AwardStep.vue';
import ConfirmStep from '@/pages/recognition/components/give-recognition/ConfirmStep.vue';
import DetailsStep from '@/pages/recognition/components/give-recognition/DetailsStep.vue';
import RecipientsStep from '@/pages/recognition/components/give-recognition/RecipientsStep.vue';
import useAwardGivingConfig from '@/pages/recognition/composables/useAwardGivingConfig';
import { budgetErrorMessage, buildAwardPayload, evaluateBudget } from '@/pages/recognition/utils/giveRecognition';
import { toCurrency } from '@/utils';
import useToast from '@/composables/useToast';

export default {
  name: 'GiveRecognitionModal',
  components: { ActiveCompanyPill, AwardStep, ConfirmStep, DetailsStep, RecipientsStep },
  props: {
    programs: {
      type: Array,
      default: () => [],
    },
    initialProgram: {
      type: Object,
      default: null,
    },
  },
  emits: ['awarded'],
  setup(props, { emit }) {
    const STEP = {
      AWARD: 'award',
      RECIPIENTS: 'recipients',
      DETAILS: 'details',
      CONFIRM: 'confirm',
    };

    const { doToast, TOAST_VARIANT } = useToast();

    const modalRef = ref(null);
    const recipientsStepRef = ref(null);
    const detailsStepRef = ref(null);

    const step = ref(STEP.AWARD);
    const selectedProgram = ref(null);
    const selectedEmployees = ref([]);
    const recipientMessage = ref('');
    const selectedCompanyValues = ref([]);
    const includeReward = ref(false);
    const selectedAmountMinor = ref(null);
    const typedAwardAmount = ref('');
    const selectedCategory = ref(null);
    const isPrivate = ref(false);
    const isSubmitting = ref(false);
    const errorMessage = ref('');
    const isCheckingBudget = ref(false);
    const companyBalanceMinor = ref(null);
    const latestPrograms = ref([]);

    const { awardAmounts, defaultAmountMinor, maxAwardValueMinor, isAdjustableAmount, categories, categoryRequired } =
      useAwardGivingConfig(selectedProgram);

    watch(
      () => props.initialProgram,
      newVal => {
        if (newVal) {
          selectedProgram.value = newVal;
          step.value = STEP.RECIPIENTS;
        }
      },
      { immediate: true },
    );

    const skipAwardSelection = computed(() => !!props.initialProgram);

    const stepSequence = computed(() => {
      const steps = skipAwardSelection.value ? [] : [STEP.AWARD];
      steps.push(STEP.RECIPIENTS, STEP.DETAILS);
      if (selectedEmployees.value.length > 1) {
        steps.push(STEP.CONFIRM);
      }
      return steps;
    });

    const displayStep = computed(() => stepSequence.value.indexOf(step.value) + 1);
    const totalSteps = computed(() => stepSequence.value.length);
    const isFinalStep = computed(() => step.value === stepSequence.value[stepSequence.value.length - 1]);

    const previousStep = computed(() => {
      const index = stepSequence.value.indexOf(step.value);
      return index > 0 ? stepSequence.value[index - 1] : null;
    });

    const nextStep = computed(() => {
      const index = stepSequence.value.indexOf(step.value);
      return index < stepSequence.value.length - 1 ? stepSequence.value[index + 1] : null;
    });

    const modalTitle = computed(() => {
      if (step.value === STEP.AWARD) {
        return 'Give award';
      }
      return `Give ${selectedProgram.value?.program_name || 'award'} award`;
    });

    const showCategorySelector = computed(() => includeReward.value && categories.value.length > 0);

    const adjustableAmountError = computed(() => {
      if (!includeReward.value || !isAdjustableAmount.value) {
        return '';
      }
      const minor = selectedAmountMinor.value;
      if (!minor || minor <= 0) {
        return 'Enter an amount greater than 0.';
      }
      if (maxAwardValueMinor.value !== null && minor > maxAwardValueMinor.value) {
        return `Amount cannot exceed ${toCurrency(maxAwardValueMinor.value)}.`;
      }
      return '';
    });

    const totalAmountMinor = computed(() =>
      includeReward.value && selectedAmountMinor.value ? selectedAmountMinor.value * selectedEmployees.value.length : 0,
    );

    const freshProgram = computed(() => {
      const id = selectedProgram.value?.recognition_award_program_id;
      return latestPrograms.value.find(p => p.recognition_award_program_id === id) ?? selectedProgram.value;
    });

    const budgetValidation = computed(() =>
      selectedProgram.value
        ? evaluateBudget({
            program: freshProgram.value,
            companyBalanceMinor: companyBalanceMinor.value,
            amountMinor: includeReward.value ? selectedAmountMinor.value : 0,
            recipientCount: selectedEmployees.value.length,
          })
        : null,
    );

    const isBudgetValid = computed(() => totalAmountMinor.value === 0 || budgetValidation.value?.is_valid !== false);

    const isDetailsComplete = computed(() => {
      if (includeReward.value && !selectedAmountMinor.value) {
        return false;
      }
      if (showCategorySelector.value && categoryRequired.value && !selectedCategory.value) {
        return false;
      }
      if (adjustableAmountError.value) {
        return false;
      }
      return isBudgetValid.value && !isCheckingBudget.value;
    });

    const canAdvance = computed(() => {
      if (isSubmitting.value) {
        return false;
      }
      if (step.value === STEP.AWARD) {
        return !!selectedProgram.value;
      }
      if (step.value === STEP.RECIPIENTS) {
        return selectedEmployees.value.length > 0;
      }
      if (step.value === STEP.DETAILS) {
        return isDetailsComplete.value;
      }
      return isBudgetValid.value && !isCheckingBudget.value;
    });

    const advanceButtonText = computed(() => {
      if (step.value === STEP.AWARD) {
        return 'Next';
      }
      if (step.value === STEP.RECIPIENTS) {
        const count = selectedEmployees.value.length;
        return `Add ${count} ${count === 1 ? 'person' : 'people'}`;
      }
      if (step.value === STEP.CONFIRM) {
        return 'Confirm and send';
      }
      if (!isFinalStep.value) {
        return `Review ${selectedEmployees.value.length} awards`;
      }
      const firstName = selectedEmployees.value[0]?.full_name?.split(' ')[0] || 'them';
      const amount = includeReward.value ? selectedAmountMinor.value : null;
      return amount ? `Send ${toCurrency(amount)} award to ${firstName}` : `Send award to ${firstName}`;
    });

    // The page's copy of the balance and of budget_spent_minor is as old as the last
    // page load, and other admins spend from the same pot, so both are re-read each
    // time the modal opens rather than trusting what the parent passed in.
    async function loadBudgetInputs() {
      isCheckingBudget.value = true;
      try {
        const [ledger, programs] = await Promise.all([
          ApiClient.getRecognitionLedger(),
          ApiClient.getRecognitionPrograms(),
        ]);
        companyBalanceMinor.value = typeof ledger?.balance_minor === 'number' ? ledger.balance_minor : null;
        latestPrograms.value = programs || [];
      } catch {
        // Without fresh figures the gauge stays hidden and the send is not blocked;
        // the API validates again on submit.
        companyBalanceMinor.value = null;
        latestPrograms.value = [];
      } finally {
        isCheckingBudget.value = false;
      }
    }

    watch(selectedProgram, () => {
      selectedCategory.value = null;
    });

    function addEmployee(employee) {
      if (!selectedEmployees.value.some(e => e.employee_id === employee.employee_id)) {
        selectedEmployees.value.push(employee);
      }
    }

    function removeEmployee(employee) {
      selectedEmployees.value = selectedEmployees.value.filter(e => e.employee_id !== employee.employee_id);
      if (selectedEmployees.value.length === 0) {
        step.value = STEP.RECIPIENTS;
      } else if (!stepSequence.value.includes(step.value)) {
        // Dropping to a single recipient removes the confirm step from the flow.
        step.value = stepSequence.value[stepSequence.value.length - 1];
      }
    }

    function onRewardToggle(checked) {
      includeReward.value = checked;
      if (checked) {
        if (defaultAmountMinor.value) {
          selectedAmountMinor.value = defaultAmountMinor.value;
        } else if (awardAmounts.value.length === 1) {
          selectedAmountMinor.value = awardAmounts.value[0];
        }
      } else {
        selectedAmountMinor.value = null;
        selectedCategory.value = null;
        typedAwardAmount.value = '';
      }
    }

    function onTypedAmountInput(value) {
      typedAwardAmount.value = value;
      const parsed = Number.parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
      selectedAmountMinor.value = Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) : null;
    }

    function onAdvance() {
      if (step.value === STEP.DETAILS && !recipientMessage.value.trim()) {
        detailsStepRef.value?.touchMessage();
        return;
      }
      if (isFinalStep.value) {
        onSubmit();
        return;
      }
      step.value = nextStep.value;
    }

    async function onSubmit() {
      errorMessage.value = '';
      isSubmitting.value = true;

      const programId = selectedProgram.value.recognition_award_program_id;
      const payload = buildAwardPayload({
        programId,
        nominatorEmployeeId: State.state.claims?.eid,
        employees: selectedEmployees.value,
        amountMinor: includeReward.value ? selectedAmountMinor.value : null,
        recipientMessage: recipientMessage.value,
        companyValues: selectedCompanyValues.value,
        category: selectedCategory.value,
        isPrivate: isPrivate.value,
      });

      try {
        await ApiClient.bulkAwardEmployees(programId, payload);
        const count = selectedEmployees.value.length;
        doToast('Success', `Award sent to ${count} ${count === 1 ? 'person' : 'people'}`, TOAST_VARIANT.SUCCESS);
        emit('awarded');
        modalRef.value?.hide();
      } catch (e) {
        const data = apiErrorData(e);
        errorMessage.value =
          budgetErrorMessage(data.error_code) ||
          data.message ||
          e?.message ||
          'Failed to send awards. Please try again.';
      } finally {
        isSubmitting.value = false;
      }
    }

    function goBack() {
      if (previousStep.value === STEP.AWARD) {
        // A different program may have a different division scope, so recipients chosen
        // under the old one can no longer be assumed eligible.
        selectedEmployees.value = [];
      }
      step.value = previousStep.value;
    }

    function resetModal() {
      if (props.initialProgram) {
        selectedProgram.value = props.initialProgram;
        step.value = STEP.RECIPIENTS;
      } else {
        selectedProgram.value = null;
        step.value = STEP.AWARD;
      }
      selectedEmployees.value = [];
      recipientMessage.value = '';
      selectedCompanyValues.value = [];
      includeReward.value = false;
      selectedAmountMinor.value = null;
      typedAwardAmount.value = '';
      selectedCategory.value = null;
      isPrivate.value = false;
      isSubmitting.value = false;
      errorMessage.value = '';
      latestPrograms.value = [];
      companyBalanceMinor.value = null;
      isCheckingBudget.value = false;
      recipientsStepRef.value?.clearSearch();
    }

    return {
      STEP,
      modalRef,
      recipientsStepRef,
      detailsStepRef,
      step,
      selectedProgram,
      selectedEmployees,
      recipientMessage,
      selectedCompanyValues,
      includeReward,
      selectedAmountMinor,
      typedAwardAmount,
      selectedCategory,
      isPrivate,
      isSubmitting,
      errorMessage,
      budgetValidation,
      isCheckingBudget,
      loadBudgetInputs,
      displayStep,
      totalSteps,
      previousStep,
      modalTitle,
      adjustableAmountError,
      canAdvance,
      advanceButtonText,
      addEmployee,
      removeEmployee,
      onRewardToggle,
      onTypedAmountInput,
      onAdvance,
      goBack,
      resetModal,
    };
  },
};
</script>

<style scoped lang="scss">
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.modal-title-stack {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.progress-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;

  &__bar {
    flex: 1;
  }

  &__text {
    font-size: 0.875rem;
    color: var(--palette-color-default-lighten-30);
    white-space: nowrap;
  }
}

:deep(.progress) {
  background-color: var(--palette-color-core-1-lighten-80);
}

:deep(.progress-bar) {
  background-color: var(--palette-color-brand-primary);
}

.step-content {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.footer-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.footer-container :deep(.btn) {
  white-space: nowrap;
  min-width: max-content;
  flex: 0 0 auto;
}
</style>
